import { Button, Input } from '@orbind/ui'
import { useEffect, useRef, useState } from 'react'

/**
 * "Adicionar outra lista" no fim do quadro, como no Trello
 * (SPEC-20261027-1920-adicionar-lista-rapida-no-quadro, pedido do Cristian:
 * "que seja uma criação rápida: abra a lista, a digitação do título e ao
 * clicar em adicionar a lista ela é criada").
 *
 * Clicar abre o campo já com o cursor; Enter ou "Adicionar lista" cria e deixa
 * o campo aberto para a próxima, como lá; Esc ou o X fecha. Status, etapas e
 * WIP da lista continuam na engrenagem.
 */
export function NovaListaRapida({ onCriar }: { onCriar: (nome: string) => Promise<unknown> }) {
  const [aberta, setAberta] = useState(false)
  const [nome, setNome] = useState('')
  const [criando, setCriando] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)
  const focar = () => caixa.current?.querySelector('input')?.focus()

  useEffect(() => {
    if (aberta) focar()
  }, [aberta])

  const fechar = () => {
    setAberta(false)
    setNome('')
  }

  const criar = async () => {
    const limpo = nome.trim()
    if (!limpo || criando) return
    setCriando(true)
    try {
      await onCriar(limpo)
      setNome('')
      // continua aberta para a próxima lista, como no Trello
      setTimeout(focar, 0)
    } catch {
      // o aviso de erro já apareceu; o nome fica no campo para tentar de novo
    } finally {
      setCriando(false)
    }
  }

  if (!aberta) {
    return (
      <button type="button" className="kb__novalista kb__novalista--botao" onClick={() => setAberta(true)}>
        <i className="fas fa-plus" aria-hidden />
        Adicionar outra lista
      </button>
    )
  }

  return (
    <div ref={caixa} className="kb__novalista" role="group" aria-label="Nova lista">
      <Input
        value={nome}
        placeholder="Digite o nome da lista…"
        aria-label="Nome da nova lista"
        maxLength={200}
        onChange={(e) => setNome(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void criar()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            fechar()
          }
        }}
      />
      <div className="kb__novalista-acoes">
        <Button loading={criando} disabled={nome.trim() === ''} onClick={() => void criar()}>
          Adicionar lista
        </Button>
        <button type="button" className="kb__novalista-fechar" aria-label="Cancelar nova lista" title="Cancelar" onClick={fechar}>
          <i className="fas fa-times" aria-hidden />
        </button>
      </div>
    </div>
  )
}
